/* MessagesInbox — /app/messaggi
   Unified inbox that merges shelter chats + match chats + map greetings so
   the user has ONE place to see conversations without reopening each shelter.

   Uses the existing endpoints:
   - GET /api/shelter/threads/me  → shelter chat threads (both roles)
   - GET /api/match/matches        → match chats (Pro only — silently skip if 402)
   - GET /api/match/greetings      → 👋 waves from map presence

   Sort key: last message timestamp (falls back to created_at).
   Empty state guides the user to Match / Mappa / Rifugi.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle, HeartHandshake, PawPrint, MapPin, Loader2, Sparkles, Hand, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WithStoryRing, { useUnseenStories } from "@/components/WithStoryRing";

const TABS = [
  { id: "all",     label: "Tutte" },
  { id: "shelter", label: "Rifugi" },
  { id: "match",   label: "Match" },
  { id: "wave",    label: "Saluti" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d2 = Math.floor(h / 24);
  return `${d2}g`;
}

export default function MessagesInbox() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shelter, setShelter] = useState([]);
  const [match, setMatch] = useState([]);
  const [waves, setWaves] = useState([]);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.get("/shelter/threads/me"),
      api.get("/match/matches"),
      api.get("/match/greetings"),
    ]);
    if (results[0].status === "fulfilled") setShelter(results[0].value.data.threads || []);
    if (results[1].status === "fulfilled") setMatch(results[1].value.data || []);
    if (results[2].status === "fulfilled") setWaves(results[2].value.data.items || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const unified = useMemo(() => {
    const rows = [];
    for (const t of shelter) {
      const iAmShelter = t.shelter_user_id === user?.id;
      rows.push({
        _kind: "shelter",
        id: `s-${t.id}`,
        title: iAmShelter ? (t.user_name || "Utente") : t.shelter_name,
        subtitle: t.subject || (iAmShelter ? "Richiesta ricevuta" : "Chat con il rifugio"),
        badge_icon: HeartHandshake,
        badge_label: iAmShelter ? "In arrivo" : "Rifugio",
        badge_color: iAmShelter ? "#7B61FF" : "#2DBA60",
        photo: iAmShelter ? t.user_photo : null,
        peer_id: iAmShelter ? t.user_id : t.shelter_user_id,
        ts: t.last_message_at || t.created_at,
        unread: t.unread || 0,
        to: `/app/rifugio/chat/${t.id}`,
      });
    }
    for (const m of match) {
      const hoursLeft = !m.expired && m.expires_at
        ? Math.max(1, Math.ceil((new Date(m.expires_at) - Date.now()) / 3600000)) : null;
      rows.push({
        _kind: "match",
        id: `m-${m.match_id}`,
        title: m.other?.name || "Match",
        subtitle: m.expired
          ? "Nessun messaggio in 3 giorni · il match è scaduto"
          : hoursLeft != null
            ? `⏳ Scade tra ${hoursLeft}h · scrivi per non perderlo`
            : (m.dogs || []).map((d) => d.name).join(" · ") || "Match ORIO",
        badge_icon: PawPrint,
        badge_label: m.expired ? "Scaduto" : "Match",
        badge_color: m.expired ? "#9A9A9A" : "#FF7A1A",
        photo: m.other?.photo_url,
        peer_id: m.other?.id,
        ts: m.last_message_at || m.created_at,
        unread: m.unread || 0,
        expired: !!m.expired,
        urgent: hoursLeft != null && hoursLeft <= 24,
        to: m.expired ? null : `/app/match?openChat=${m.match_id}`,
      });
    }
    for (const w of waves) {
      rows.push({
        _kind: "wave",
        id: `w-${w.id}`,
        title: w.sender?.name || "Utente",
        subtitle: w.dog?.name ? `🐾 ${w.dog.name}` : (w.sender?.city || "Ti ha salutato dalla mappa"),
        badge_icon: Hand,
        badge_label: "Saluto",
        badge_color: "#F4B83C",
        photo: w.sender?.photo_url,
        peer_id: w.sender?.id,
        ts: w.created_at,
        unread: w.seen_by_recipient ? 0 : 1,
        to: `/app/mappa?open=presence`,
      });
    }
    return rows.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  }, [shelter, match, waves, user?.id]);

  const filtered = tab === "all" ? unified : unified.filter((r) => r._kind === tab);
  const peerIds = filtered.map((r) => r.peer_id).filter(Boolean);
  const { unseen } = useUnseenStories(peerIds);

  const totals = useMemo(() => ({
    all: unified.length,
    shelter: unified.filter((r) => r._kind === "shelter").length,
    match: unified.filter((r) => r._kind === "match").length,
    wave: unified.filter((r) => r._kind === "wave").length,
  }), [unified]);

  return (
    <div className="w-full max-w-2xl mx-auto p-4 sm:p-6 pb-[132px] md:pb-8" data-testid="messages-inbox">
      <div className="mb-4">
        <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">
          <MessageCircle className="h-3 w-3" /> Inbox ORIO
        </div>
        <h1 className="mt-1 font-display text-3xl leading-tight">Messaggi</h1>
        <p className="mt-1 text-[13px] text-[#7A7A7A]">
          Tutte le conversazioni in un unico posto — rifugi, match e saluti dalla mappa.
        </p>
      </div>

      {/* Bulk clear — one-tap svuota inbox. */}
      {unified.length > 0 && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm("Svuotare l'intera inbox? Sparisce da te — l'altra persona vede ancora le conversazioni.")) return;
              try {
                await api.delete("/inbox/all");
                setShelter([]); setMatch([]); setWaves([]);
                toast.success("Inbox svuotata");
              } catch { toast.error("Non riuscito, riprova"); }
            }}
            data-testid="inbox-clear-all"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-medium border"
            style={{ background: "#FEF2F2", borderColor: "#FCA5A5", color: "#B91C1C" }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Svuota inbox
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="inbox-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`inbox-tab-${t.id}`}
              className="shrink-0 px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold border transition"
              style={{
                background: active ? "#FF7A1A" : "white",
                borderColor: active ? "#FF7A1A" : "#F0EBE3",
                color: active ? "white" : "#1A1A1A",
              }}
            >
              {t.label}
              <span className={`ml-1.5 text-[10.5px] font-bold ${active ? "text-white/85" : "text-[#9A9A9A]"}`}>
                {totals[t.id] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#FF7A1A]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState tab={tab} />
      ) : (
        <div className="space-y-2">
          {filtered.map((row) => (
            <InboxRow key={row.id} row={row} unseen={unseen}
              onDelete={async () => {
                const kind = row._kind;
                const rawId = String(row.id || "").replace(/^[msw]-/, "");
                try {
                  if (kind === "match") await api.delete(`/match/${rawId}`);
                  else if (kind === "shelter") await api.delete(`/shelter/threads/${rawId}`);
                  else if (kind === "wave") await api.delete(`/match/greetings/${rawId}`);
                  // Optimistic UI: reload sources.
                  await load();
                  toast.success("Conversazione rimossa");
                } catch { toast.error("Non riuscito"); }
              }} />
          ))}
        </div>
      )}
    </div>
  );
}

function InboxRow({ row, unseen, onDelete }) {
  const Icon = row.badge_icon;
  const Wrapper = row.to ? Link : "div";
  return (
    <div className={`rounded-2xl bg-white border relative transition ${row.expired ? "border-[#E5E0D8] opacity-80" : row.urgent ? "border-[#F4B83C]" : "border-[#F0EBE3] hover:shadow-md"}`}>
      {row.expired && (
        <div data-testid={`inbox-expired-banner-${row.id}`}
          className="px-3 py-1.5 rounded-t-2xl bg-[#F4EFE6] text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-wide">
          ⌛ Match scaduto · sparirà entro 24h
        </div>
      )}
    <div className="flex items-center gap-3 p-3">
      <Wrapper
        {...(row.to ? { to: row.to } : {})}
        data-testid={`inbox-row-${row._kind}-${row.id}`}
        className={`flex items-center gap-3 flex-1 min-w-0 ${row.expired ? "grayscale" : ""}`}
      >
        <WithStoryRing unseen={unseen} userId={row.peer_id} size={52}>
          {row.photo ? (
            <img src={row.photo} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-[#F5EFE6]">
              <span className="font-display text-[15px] text-[#FF7A1A] font-semibold">
                {(row.title || "?").trim().charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </WithStoryRing>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9.5px] font-bold uppercase tracking-wide text-white shrink-0"
              style={{ background: row.badge_color }}
            >
              <Icon className="h-2.5 w-2.5" /> {row.badge_label}
            </span>
            <span className="text-[13.5px] font-semibold text-[#1A1A1A] truncate">{row.title}</span>
          </div>
          <div className="text-[11.5px] text-[#7A7A7A] truncate mt-0.5">{row.subtitle}</div>
        </div>

        <div className="text-right shrink-0">
          <div className="text-[10.5px] text-[#9A9A9A]">{timeAgo(row.ts)}</div>
          {row.unread > 0 && (
            <div className="mt-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold text-white"
              style={{ background: "#E11D48" }}>
              {row.unread > 9 ? "9+" : row.unread}
            </div>
          )}
        </div>
      </Wrapper>
      {/* Delete button — outside the Link so tap doesn't navigate */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (window.confirm("Rimuovere questa conversazione dalla tua inbox?")) onDelete?.();
        }}
        aria-label="Elimina conversazione"
        data-testid={`inbox-delete-${row.id}`}
        className="h-8 w-8 rounded-full flex items-center justify-center text-[#9A9A9A] hover:text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors shrink-0"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
    </div>
  );
}

function EmptyState({ tab }) {
  const map = {
    all:     { icon: MessageCircle, title: "Nessun messaggio ancora", body: "Le tue chat appariranno qui." },
    shelter: { icon: HeartHandshake, title: "Nessuna chat con rifugi", body: "Apri un rifugio dalla mappa e tocca Contatta." },
    match:   { icon: PawPrint, title: "Nessun match ancora", body: "Prova la modalità Match per conoscere altri padroni.", to: "/app/match" },
    wave:    { icon: Hand, title: "Nessun saluto ricevuto", body: "Attiva la visibilità dalla mappa per ricevere 👋 nei tuoi 25km.", to: "/app/mappa" },
  };
  const s = map[tab] || map.all;
  const Icon = s.icon;
  return (
    <div className="rounded-3xl bg-white border border-[#F0EBE3] p-10 text-center flex flex-col items-center gap-3">
      <div className="h-14 w-14 rounded-full flex items-center justify-center"
        style={{ background: "linear-gradient(135deg,#FFF6E6,#FFE8CB)", border: "1px solid #F0D9B0" }}>
        <Icon className="h-6 w-6 text-[#C25612]" />
      </div>
      <div>
        <div className="font-display text-xl leading-tight">{s.title}</div>
        <div className="text-[13px] text-[#7A7A7A] mt-1">{s.body}</div>
      </div>
      {s.to && (
        <Link to={s.to}
          className="mt-1 inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12.5px] font-bold text-white"
          style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
          <Sparkles className="h-3.5 w-3.5" /> Esplora
        </Link>
      )}
    </div>
  );
}
